/**
 * MobileMileagePage - QuickBooks-inspired mileage tracking for mobile
 * 
 * A mobile-first mileage tracking experience inspired by QuickBooks Mileage Tracker.
 * Features: Swipe-to-classify trips, automatic GPS tracking, vehicle management,
 * tax deduction calculator, simple tabs (Unreviewed/Business/Personal).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { 
  Car, MapPin, Clock, DollarSign, Download, Plus, Trash2,
  ChevronRight, ChevronDown, ChevronUp, Loader2, Settings,
  Briefcase, Coffee, CheckCircle2, XCircle, Route, Calendar,
  Gauge, Navigation, AlertCircle, Smartphone, MoreVertical,
  RefreshCw, Edit2, ChevronLeft
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import api from '../../lib/api';
import { TrackingControl } from '../../components/tracking/TrackingControl';

// IRS standard mileage rate for 2026
const IRS_RATE = 0.725;

// Helper function to format date
function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric' 
    });
  } catch {
    return dateStr;
  }
}

// Format time from ISO string
function formatTime(isoString) {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  } catch {
    return '';
  }
}

// Calculate deduction
function calculateDeduction(miles, rate = IRS_RATE) {
  return (miles * rate).toFixed(2);
}

// Trip Card Component with swipe gestures
function TripCard({ entry, onClassify, onView, onDelete }) {
  const [touchStart, setTouchStart] = useState(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [expanded, setExpanded] = useState(false);
  
  const isUnreviewed = !entry.is_reviewed;
  const tripType = entry.trip_type || 'unclassified';
  
  const handleTouchStart = (e) => {
    if (!isUnreviewed) return;
    setTouchStart(e.touches[0].clientX);
  };
  
  const handleTouchMove = (e) => {
    if (!touchStart || !isUnreviewed) return;
    const diff = e.touches[0].clientX - touchStart;
    setSwipeOffset(Math.max(-120, Math.min(120, diff)));
  };
  
  const handleTouchEnd = () => {
    if (!isUnreviewed) {
      setSwipeOffset(0);
      setTouchStart(null);
      return;
    }
    
    if (swipeOffset > 80) {
      // Swipe right - Business
      onClassify(entry.id, 'business');
    } else if (swipeOffset < -80) {
      // Swipe left - Personal
      onClassify(entry.id, 'personal');
    }
    setSwipeOffset(0);
    setTouchStart(null);
  };
  
  const miles = entry.miles || entry.segments?.reduce((sum, s) => sum + (s.miles || 0), 0) || 0;
  const deduction = tripType === 'business' ? calculateDeduction(miles) : 0;
  
  return (
    <div 
      className="relative overflow-hidden mb-3"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Swipe indicators - only show for unreviewed */}
      {isUnreviewed && (
        <>
          <div 
            className="absolute inset-y-0 left-0 w-24 bg-teal-500 flex items-center justify-center transition-opacity"
            style={{ opacity: swipeOffset > 40 ? 1 : 0 }}
          >
            <div className="text-white text-center">
              <Briefcase className="h-6 w-6 mx-auto mb-1" />
              <span className="text-xs font-medium">Business</span>
            </div>
          </div>
          <div 
            className="absolute inset-y-0 right-0 w-24 bg-slate-500 flex items-center justify-center transition-opacity"
            style={{ opacity: swipeOffset < -40 ? 1 : 0 }}
          >
            <div className="text-white text-center">
              <Coffee className="h-6 w-6 mx-auto mb-1" />
              <span className="text-xs font-medium">Personal</span>
            </div>
          </div>
        </>
      )}
      
      {/* Main card */}
      <Card 
        className={`transition-transform bg-white dark:bg-slate-800/80 border-l-4 
                   ${tripType === 'business' ? 'border-l-teal-500' : 
                     tripType === 'personal' ? 'border-l-slate-400' : 
                     'border-l-amber-500'}`}
        style={{ transform: `translateX(${swipeOffset}px)` }}
        data-testid={`trip-card-${entry.id}`}
      >
        <CardContent className="p-3">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0" onClick={() => onView(entry)}>
              {/* Date and badges */}
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs text-slate-500">{formatDate(entry.date)}</span>
                {entry.is_auto_tracked && (
                  <Badge variant="outline" className="text-xs px-1.5 py-0 border-blue-500 text-blue-500">
                    GPS
                  </Badge>
                )}
                {entry.route_name && (
                  <Badge variant="outline" className="text-xs px-1.5 py-0">
                    Route
                  </Badge>
                )}
                {!isUnreviewed && (
                  <Badge 
                    variant={tripType === 'business' ? 'default' : 'secondary'}
                    className={`text-xs px-1.5 py-0 ${tripType === 'business' ? 'bg-teal-500' : 'bg-slate-500'}`}
                  >
                    {tripType === 'business' ? 'Business' : 'Personal'}
                  </Badge>
                )}
              </div>
              
              {/* Route info */}
              <div className="flex items-center gap-1 text-sm">
                <span className="text-slate-900 dark:text-white truncate">
                  {entry.start_address?.split(',')[0] || 'Unknown'}
                </span>
                <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
                <span className="text-slate-900 dark:text-white truncate">
                  {entry.end_address?.split(',')[0] || entry.segments?.[entry.segments.length - 1]?.to_address?.split(',')[0] || 'Unknown'}
                </span>
              </div>
              
              {/* Time */}
              {entry.start_time && (
                <p className="text-xs text-slate-500 mt-1">
                  {formatTime(entry.start_time)} - {formatTime(entry.end_time)}
                </p>
              )}
            </div>
            
            {/* Miles and deduction */}
            <div className="text-right shrink-0 ml-3">
              <p className="font-semibold text-slate-900 dark:text-white">
                {miles.toFixed(1)} mi
              </p>
              {tripType === 'business' && (
                <p className="text-xs text-green-500 font-medium">
                  ${deduction}
                </p>
              )}
            </div>
          </div>
          
          {/* Action buttons for unreviewed trips */}
          {isUnreviewed && (
            <div className="flex gap-2 mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
              <Button 
                size="sm" 
                className="flex-1 bg-teal-600 hover:bg-teal-700"
                onClick={(e) => { e.stopPropagation(); onClassify(entry.id, 'business'); }}
                data-testid={`classify-business-${entry.id}`}
              >
                <Briefcase className="h-4 w-4 mr-1" />
                Business
              </Button>
              <Button 
                size="sm"
                variant="outline"
                className="flex-1"
                onClick={(e) => { e.stopPropagation(); onClassify(entry.id, 'personal'); }}
                data-testid={`classify-personal-${entry.id}`}
              >
                <Coffee className="h-4 w-4 mr-1" />
                Personal
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Stats Card Component
function StatsCard({ label, value, icon: Icon, subtext, highlight }) {
  return (
    <div className={`p-3 rounded-lg ${highlight ? 'bg-teal-500/10 border border-teal-500/30' : 'bg-slate-100 dark:bg-slate-800'}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`h-4 w-4 ${highlight ? 'text-teal-500' : 'text-slate-500'}`} />
        <span className="text-xs text-slate-500">{label}</span>
      </div>
      <p className={`text-lg font-bold ${highlight ? 'text-teal-500' : 'text-slate-900 dark:text-white'}`}>
        {value}
      </p>
      {subtext && (
        <p className="text-xs text-slate-500 mt-0.5">{subtext}</p>
      )}
    </div>
  );
}

// Vehicle Card Component
function VehicleCard({ vehicle, isDefault, onSetDefault, onEdit, onDelete }) {
  return (
    <Card className={`${isDefault ? 'border-teal-500 border-2' : ''}`}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDefault ? 'bg-teal-500' : 'bg-slate-200 dark:bg-slate-700'}`}>
              <Car className={`h-5 w-5 ${isDefault ? 'text-white' : 'text-slate-500'}`} />
            </div>
            <div>
              <p className="font-medium text-slate-900 dark:text-white">
                {vehicle.year} {vehicle.make} {vehicle.model}
              </p>
              <p className="text-xs text-slate-500">
                {vehicle.odometer?.toLocaleString() || 0} miles
              </p>
            </div>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {!isDefault && (
                <DropdownMenuItem onClick={() => onSetDefault(vehicle.id)}>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Set as Default
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => onEdit(vehicle)}>
                <Edit2 className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onDelete(vehicle.id)} className="text-red-500">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {isDefault && (
          <Badge className="mt-2 bg-teal-500">Default Vehicle</Badge>
        )}
      </CardContent>
    </Card>
  );
}

// Main Mobile Mileage Page Component
export default function MobileMileagePage({ profile }) {
  // View state
  const [activeTab, setActiveTab] = useState('unreviewed'); // 'unreviewed', 'business', 'personal'
  const [showVehicles, setShowVehicles] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showTripDetail, setShowTripDetail] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  
  // Data state
  const [entries, setEntries] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [defaultVehicleId, setDefaultVehicleId] = useState(null);
  const [stats, setStats] = useState({ today: 0, week: 0, month: 0, year: 0 });
  const [loading, setLoading] = useState(true);
  
  // Settings
  const [irsRate, setIrsRate] = useState(IRS_RATE);
  
  // Vehicle dialog
  const [showVehicleDialog, setShowVehicleDialog] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [vehicleForm, setVehicleForm] = useState({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    odometer: 0
  });

  // Fetch mileage entries
  const fetchEntries = useCallback(async () => {
    try {
      const now = new Date();
      const response = await api.getMileageLog(now.getFullYear(), now.getMonth() + 1);
      setEntries(response.data.entries || []);
    } catch (error) {
      console.error('Failed to fetch mileage entries:', error);
    }
  }, []);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/api/mileage-stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  // Fetch vehicles
  const fetchVehicles = useCallback(async () => {
    try {
      const response = await api.getVehicles();
      setVehicles(response.data.vehicles || []);
      setDefaultVehicleId(response.data.default_vehicle_id);
    } catch (error) {
      console.error('Failed to fetch vehicles:', error);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchEntries(), fetchStats(), fetchVehicles()]);
      setLoading(false);
    };
    loadData();
  }, [fetchEntries, fetchStats, fetchVehicles]);

  // Classify trip
  const handleClassifyTrip = async (entryId, tripType) => {
    try {
      await api.markMileageReviewed(entryId, true);
      
      // Update the entry's trip type locally
      setEntries(entries.map(e => 
        e.id === entryId 
          ? { ...e, is_reviewed: true, trip_type: tripType }
          : e
      ));
      
      toast.success(`Marked as ${tripType === 'business' ? 'Business' : 'Personal'}`);
      
      // Refresh stats
      fetchStats();
    } catch (error) {
      toast.error('Failed to classify trip');
    }
  };

  // View trip detail
  const handleViewTrip = (entry) => {
    setSelectedTrip(entry);
    setShowTripDetail(true);
  };

  // Delete trip
  const handleDeleteTrip = async (entryId) => {
    try {
      await api.deleteMileageEntry(entryId);
      setEntries(entries.filter(e => e.id !== entryId));
      toast.success('Trip deleted');
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete trip');
    }
  };

  // Vehicle management
  const handleAddVehicle = async () => {
    try {
      const response = await api.createVehicle(vehicleForm);
      setVehicles([...vehicles, response.data]);
      setShowVehicleDialog(false);
      setVehicleForm({ make: '', model: '', year: new Date().getFullYear(), odometer: 0 });
      toast.success('Vehicle added!');
    } catch (error) {
      toast.error('Failed to add vehicle');
    }
  };

  const handleUpdateVehicle = async () => {
    if (!editingVehicle) return;
    try {
      await api.updateVehicle(editingVehicle.id, vehicleForm);
      setVehicles(vehicles.map(v => 
        v.id === editingVehicle.id ? { ...v, ...vehicleForm } : v
      ));
      setShowVehicleDialog(false);
      setEditingVehicle(null);
      setVehicleForm({ make: '', model: '', year: new Date().getFullYear(), odometer: 0 });
      toast.success('Vehicle updated!');
    } catch (error) {
      toast.error('Failed to update vehicle');
    }
  };

  const handleSetDefaultVehicle = async (vehicleId) => {
    try {
      await api.setDefaultVehicle(vehicleId);
      setDefaultVehicleId(vehicleId);
      toast.success('Default vehicle set!');
    } catch (error) {
      toast.error('Failed to set default vehicle');
    }
  };

  const handleDeleteVehicle = async (vehicleId) => {
    try {
      await api.deleteVehicle(vehicleId);
      setVehicles(vehicles.filter(v => v.id !== vehicleId));
      if (defaultVehicleId === vehicleId) {
        setDefaultVehicleId(null);
      }
      toast.success('Vehicle deleted');
    } catch (error) {
      toast.error('Failed to delete vehicle');
    }
  };

  const openEditVehicle = (vehicle) => {
    setEditingVehicle(vehicle);
    setVehicleForm({
      make: vehicle.make || '',
      model: vehicle.model || '',
      year: vehicle.year || new Date().getFullYear(),
      odometer: vehicle.odometer || 0
    });
    setShowVehicleDialog(true);
  };

  // Filter entries by tab
  const filteredEntries = entries.filter(entry => {
    if (activeTab === 'unreviewed') return !entry.is_reviewed;
    if (activeTab === 'business') return entry.is_reviewed && entry.trip_type === 'business';
    if (activeTab === 'personal') return entry.is_reviewed && entry.trip_type === 'personal';
    return true;
  });

  // Calculate totals
  const unreviewedCount = entries.filter(e => !e.is_reviewed).length;
  const businessMiles = entries
    .filter(e => e.is_reviewed && e.trip_type === 'business')
    .reduce((sum, e) => sum + (e.miles || e.segments?.reduce((s, seg) => s + (seg.miles || 0), 0) || 0), 0);
  const totalDeduction = calculateDeduction(businessMiles);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900" data-testid="mobile-mileage-page">
      {/* Header */}
      <div className="p-4 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Route className="h-5 w-5 text-teal-500" />
              Mileage
            </h1>
            <p className="text-sm text-slate-500">Track and classify your trips</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowVehicles(true)}
              data-testid="vehicles-btn"
            >
              <Car className="h-4 w-4" />
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowSettings(true)}
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {/* GPS Tracking Control - Only shows on native */}
        <TrackingControl />
        
        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-2 mt-4">
          <StatsCard 
            label="This Month" 
            value={`${stats.month.toFixed(1)} mi`}
            icon={Calendar}
          />
          <StatsCard 
            label="Tax Deduction" 
            value={`$${totalDeduction}`}
            icon={DollarSign}
            highlight
            subtext={`${businessMiles.toFixed(1)} business mi`}
          />
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <button
          className={`flex-1 py-3 text-sm font-medium relative ${activeTab === 'unreviewed' ? 'text-teal-500' : 'text-slate-500'}`}
          onClick={() => setActiveTab('unreviewed')}
          data-testid="tab-unreviewed"
        >
          Unreviewed
          {unreviewedCount > 0 && (
            <Badge className="ml-1 bg-amber-500 text-xs px-1.5">{unreviewedCount}</Badge>
          )}
          {activeTab === 'unreviewed' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-500" />
          )}
        </button>
        <button
          className={`flex-1 py-3 text-sm font-medium relative ${activeTab === 'business' ? 'text-teal-500' : 'text-slate-500'}`}
          onClick={() => setActiveTab('business')}
          data-testid="tab-business"
        >
          Business
          {activeTab === 'business' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-500" />
          )}
        </button>
        <button
          className={`flex-1 py-3 text-sm font-medium relative ${activeTab === 'personal' ? 'text-teal-500' : 'text-slate-500'}`}
          onClick={() => setActiveTab('personal')}
          data-testid="tab-personal"
        >
          Personal
          {activeTab === 'personal' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-500" />
          )}
        </button>
      </div>
      
      {/* Trip List */}
      <div className="flex-1 overflow-y-auto p-4">
        {filteredEntries.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-center">
            {activeTab === 'unreviewed' ? (
              <>
                <CheckCircle2 className="h-12 w-12 text-green-500 mb-3" />
                <h3 className="font-medium text-slate-700 dark:text-slate-300">All caught up!</h3>
                <p className="text-sm text-slate-500 mt-1">No trips to review</p>
              </>
            ) : (
              <>
                <Route className="h-12 w-12 text-slate-400 mb-3" />
                <h3 className="font-medium text-slate-700 dark:text-slate-300">No {activeTab} trips</h3>
                <p className="text-sm text-slate-500 mt-1">
                  {activeTab === 'business' 
                    ? 'Swipe trips right to mark as business'
                    : 'Swipe trips left to mark as personal'}
                </p>
              </>
            )}
          </div>
        ) : (
          <>
            {/* Swipe hint for unreviewed */}
            {activeTab === 'unreviewed' && filteredEntries.length > 0 && (
              <div className="flex items-center justify-center gap-4 mb-4 py-2 px-3 bg-slate-100 dark:bg-slate-800 rounded-lg text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <ChevronRight className="h-3 w-3" />
                  Swipe right = Business
                </span>
                <span className="flex items-center gap-1">
                  <ChevronLeft className="h-3 w-3" />
                  Swipe left = Personal
                </span>
              </div>
            )}
            
            {filteredEntries.map(entry => (
              <TripCard
                key={entry.id}
                entry={entry}
                onClassify={handleClassifyTrip}
                onView={handleViewTrip}
                onDelete={handleDeleteTrip}
              />
            ))}
          </>
        )}
      </div>
      
      {/* Export button for business trips */}
      {activeTab === 'business' && filteredEntries.length > 0 && (
        <div className="p-4 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 safe-area-bottom">
          <Button 
            className="w-full bg-teal-600 hover:bg-teal-700"
            onClick={() => toast.info('Export coming soon!')}
            data-testid="export-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Export Business Trips (CSV)
          </Button>
        </div>
      )}

      {/* Vehicles Sheet */}
      <Dialog open={showVehicles} onOpenChange={setShowVehicles}>
        <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Car className="h-5 w-5" />
              My Vehicles
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {vehicles.length === 0 ? (
              <div className="text-center py-8">
                <Car className="h-10 w-10 text-slate-400 mx-auto mb-2" />
                <p className="text-slate-500">No vehicles added yet</p>
              </div>
            ) : (
              vehicles.map(vehicle => (
                <VehicleCard
                  key={vehicle.id}
                  vehicle={vehicle}
                  isDefault={vehicle.id === defaultVehicleId}
                  onSetDefault={handleSetDefaultVehicle}
                  onEdit={openEditVehicle}
                  onDelete={handleDeleteVehicle}
                />
              ))
            )}
            
            <Button 
              className="w-full mt-4"
              onClick={() => {
                setEditingVehicle(null);
                setVehicleForm({ make: '', model: '', year: new Date().getFullYear(), odometer: 0 });
                setShowVehicleDialog(true);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Vehicle
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Vehicle Dialog */}
      <Dialog open={showVehicleDialog} onOpenChange={setShowVehicleDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingVehicle ? 'Edit Vehicle' : 'Add Vehicle'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Make</Label>
                <Input
                  placeholder="Toyota"
                  value={vehicleForm.make}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, make: e.target.value })}
                />
              </div>
              <div>
                <Label>Model</Label>
                <Input
                  placeholder="Camry"
                  value={vehicleForm.model}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, model: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Year</Label>
                <Input
                  type="number"
                  min={1990}
                  max={2030}
                  value={vehicleForm.year}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, year: parseInt(e.target.value) || 2024 })}
                />
              </div>
              <div>
                <Label>Odometer</Label>
                <Input
                  type="number"
                  min={0}
                  value={vehicleForm.odometer}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, odometer: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVehicleDialog(false)}>Cancel</Button>
            <Button 
              onClick={editingVehicle ? handleUpdateVehicle : handleAddVehicle}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {editingVehicle ? 'Update' : 'Add'} Vehicle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Mileage Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>IRS Mileage Rate (per mile)</Label>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-slate-500">$</span>
                <Input
                  type="number"
                  step="0.001"
                  min={0}
                  value={irsRate}
                  onChange={(e) => setIrsRate(parseFloat(e.target.value) || IRS_RATE)}
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                2026 IRS standard rate: $0.725/mile
              </p>
            </div>
            
            <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
              <h4 className="font-medium text-slate-900 dark:text-white mb-2">
                Tracking Permissions
              </h4>
              <p className="text-xs text-slate-500 mb-3">
                For accurate 24/7 tracking, make sure location is set to "Always Allow" in your device settings.
              </p>
              <Button 
                variant="outline" 
                size="sm"
                className="w-full"
                onClick={() => {
                  // This would open device settings on native
                  toast.info('Open your device Settings > Apps > Servio > Permissions > Location > Allow all the time');
                }}
              >
                <Smartphone className="h-4 w-4 mr-2" />
                Check Permissions
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowSettings(false)} className="bg-teal-600 hover:bg-teal-700">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Trip Detail Dialog */}
      <Dialog open={showTripDetail} onOpenChange={setShowTripDetail}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Trip Details</DialogTitle>
          </DialogHeader>
          {selectedTrip && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-slate-500">Date</Label>
                <p className="font-medium">{formatDate(selectedTrip.date)}</p>
              </div>
              
              <div>
                <Label className="text-slate-500">From</Label>
                <p className="font-medium">{selectedTrip.start_address || 'Unknown'}</p>
              </div>
              
              <div>
                <Label className="text-slate-500">To</Label>
                <p className="font-medium">
                  {selectedTrip.end_address || selectedTrip.segments?.[selectedTrip.segments.length - 1]?.to_address || 'Unknown'}
                </p>
              </div>
              
              {selectedTrip.segments && selectedTrip.segments.length > 1 && (
                <div>
                  <Label className="text-slate-500">Stops</Label>
                  <p className="font-medium">{selectedTrip.segments.length} stops</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Distance</Label>
                  <p className="font-medium text-lg">
                    {(selectedTrip.miles || selectedTrip.segments?.reduce((s, seg) => s + (seg.miles || 0), 0) || 0).toFixed(1)} miles
                  </p>
                </div>
                {selectedTrip.trip_type === 'business' && (
                  <div>
                    <Label className="text-slate-500">Deduction</Label>
                    <p className="font-medium text-lg text-green-500">
                      ${calculateDeduction(selectedTrip.miles || selectedTrip.segments?.reduce((s, seg) => s + (seg.miles || 0), 0) || 0)}
                    </p>
                  </div>
                )}
              </div>
              
              {selectedTrip.is_auto_tracked && (
                <Badge className="bg-blue-500">Automatically Tracked via GPS</Badge>
              )}
              
              {selectedTrip.route_name && (
                <div>
                  <Label className="text-slate-500">Route</Label>
                  <p className="font-medium">{selectedTrip.route_name}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button 
              variant="outline" 
              className="text-red-500 border-red-500"
              onClick={() => {
                if (selectedTrip) {
                  handleDeleteTrip(selectedTrip.id);
                  setShowTripDetail(false);
                }
              }}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
            <Button onClick={() => setShowTripDetail(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
